import { ApiError, providerApi, type CreatePresignedUploadInput } from "../../lib/api";
import type { TranslationFunction } from "../../lib/preferences";

export const providerImageMaxBytes = 3 * 1024 * 1024;
const maxImageDimension = 1600;

export function formatProviderImageBytes(value: number) {
  if (value < 1024 * 1024) {
    return `${Math.ceil(value / 1024)} KB`;
  }

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

async function canvasToBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/webp", quality);
  });
}

function decodeImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not read this image."));
    image.src = source;
  });
}

function readImageAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }

      reject(new Error("Could not read this image."));
    };
    reader.onerror = () => reject(new Error("Could not read this image."));
    reader.onabort = () => reject(new Error("Could not read this image."));
    reader.readAsDataURL(file);
  });
}

async function decodeProviderImage(file: File) {
  const imageUrl = URL.createObjectURL(file);
  try {
    try {
      return await decodeImage(imageUrl);
    } catch {
      return decodeImage(await readImageAsDataUrl(file));
    }
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

export async function prepareProviderImage(file: File) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Choose an image file.");
  }

  if (file.type === "image/webp" && file.size <= providerImageMaxBytes) {
    return file;
  }

  let image: HTMLImageElement;
  try {
    image = await decodeProviderImage(file);
  } catch {
    if (file.size <= providerImageMaxBytes) {
      return file;
    }

    throw new Error("Could not read this image and it is larger than 3 MB.");
  }

  const scale = Math.min(1, maxImageDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Image compression is not available in this browser.");
  }

  context.drawImage(image, 0, 0, width, height);

  for (const quality of [0.82, 0.72, 0.62]) {
    const blob = await canvasToBlob(canvas, quality);
    if (blob && blob.size <= providerImageMaxBytes) {
      return new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), { type: "image/webp" });
    }
  }

  if (file.size <= providerImageMaxBytes) {
    return file;
  }

  throw new Error("Image is still larger than 3 MB after compression.");
}

export function getProviderImageErrorMessage(error: unknown, t: TranslationFunction) {
  if (error instanceof ApiError) {
    return error.detail;
  }

  if (error instanceof Error) {
    return t(error.message);
  }

  return t("Image upload failed.");
}

export async function uploadProviderImage(
  ownerType: CreatePresignedUploadInput["owner_type"],
  ownerId: string,
  file: File,
) {
  const preparedFile = await prepareProviderImage(file);
  const signed = await providerApi.createPresignedUpload({
    content_type: preparedFile.type,
    filename: preparedFile.name,
    owner_id: ownerId,
    owner_type: ownerType,
    size_bytes: preparedFile.size,
  });

  const uploadResponse = await fetch(signed.upload_url, {
    body: preparedFile,
    headers: signed.headers,
    method: "PUT",
  });

  if (!uploadResponse.ok) {
    throw new Error("Direct upload failed before gaamed could save the image.");
  }

  return {
    file: preparedFile,
    signed,
  };
}
